import type {
    EventModuleEvent,
    EventInfoSession,
    EventSpeaker,
    EventParticipant,
    CreateEventPayload,
    UpdateEventPayload,
    CreateSpeakerPayload,
    UpdateSpeakerPayload,
    EventMutationResponse,
    SpeakerMutationResponse,
    EventReferenceData,
    UpdateParticipantPayload,
    ParticipantMutationResponse,
} from '~/types/api/client/eventmodule'

/** Standard error envelope used across the service layer. */
type ServiceError = { status: number; message: string }

/**
 * Client-side service for the Event Module feature.
 *
 * Mirrors the existing service-layer pattern (see EventsAPI):
 *   new API().client(clientId).compiere.eventModule.getEvents()
 *
 * SCAFFOLDING: methods are thin $fetch wrappers around the
 * /api/client/eventmodule/* server routes. Business logic / mapping lives in
 * the server handlers and components, not here.
 */
export class EventModuleAPI {
    clientId: number | string

    constructor(clientId: number | string) {
        this.clientId = clientId
    }

    async getReferenceData(): Promise<EventReferenceData | ServiceError> {
        return await $fetch<EventReferenceData>(`/api/client/eventmodule/referencedata`, {
            headers: useRequestHeaders(['cookie']),
            query: { clientid: this.clientId },
        }).catch((err) => ({ status: err.statusCode ?? 0, message: err.message }))
    }

    async getEvents(): Promise<EventModuleEvent[] | ServiceError> {
        return await $fetch<EventModuleEvent[]>(`/api/client/eventmodule/events`, {
            headers: useRequestHeaders(['cookie']),
            query: { clientid: this.clientId },
        }).catch((err) => ({ status: err.statusCode ?? 0, message: err.message }))
    }

    async getEventOverview(webuserId: number): Promise<EventInfoSession[] | ServiceError> {
        return await $fetch<EventInfoSession[]>(`/api/client/eventmodule/eventoverview`, {
            headers: useRequestHeaders(['cookie']),
            query: { clientid: this.clientId, webuserid: webuserId },
        }).catch((err) => ({ status: err.statusCode ?? 0, message: err.message }))
    }

    async getEventInfo(eventId: number): Promise<EventInfoSession[] | ServiceError> {
        return await $fetch<EventInfoSession[]>(`/api/client/eventmodule/eventinfo`, {
            headers: useRequestHeaders(['cookie']),
            query: { clientid: this.clientId, eventid: eventId },
        }).catch((err) => ({ status: err.statusCode ?? 0, message: err.message }))
    }

    async getEventMaster(eventId: number): Promise<Partial<EventModuleEvent> | ServiceError> {
        return await $fetch<Partial<EventModuleEvent>>(`/api/client/eventmodule/eventmaster`, {
            headers: useRequestHeaders(['cookie']),
            query: { clientid: this.clientId, eventid: eventId },
        }).catch((err) => ({ status: err.statusCode ?? 0, message: err.message }))
    }

    async createEvent(payload: CreateEventPayload): Promise<EventMutationResponse | ServiceError> {
        return await $fetch<EventMutationResponse>(`/api/client/eventmodule/event`, {
            method: 'POST',
            headers: useRequestHeaders(['cookie']),
            body: { clientId: this.clientId, payload },
        }).catch((err) => ({ status: err.statusCode ?? 0, message: err.message }))
    }

    async updateEvent(payload: UpdateEventPayload): Promise<EventMutationResponse | ServiceError> {
        return await $fetch<EventMutationResponse>(`/api/client/eventmodule/event`, {
            method: 'PUT',
            headers: useRequestHeaders(['cookie']),
            body: { clientId: this.clientId, payload },
        }).catch((err) => ({ status: err.statusCode ?? 0, message: err.message }))
    }

    async getAllSpeakers(): Promise<EventSpeaker[] | ServiceError> {
        return await $fetch<EventSpeaker[]>(`/api/client/eventmodule/speakers`, {
            headers: useRequestHeaders(['cookie']),
            query: { clientid: this.clientId },
        }).catch((err) => ({ status: err.statusCode ?? 0, message: err.message }))
    }

    async getSpeakerOverview(eventDataId: number): Promise<EventSpeaker[] | ServiceError> {
        return await $fetch<EventSpeaker[]>(`/api/client/eventmodule/speakeroverview`, {
            headers: useRequestHeaders(['cookie']),
            query: { clientid: this.clientId, eventdataid: eventDataId },
        }).catch((err) => ({ status: err.statusCode ?? 0, message: err.message }))
    }

    async createSpeaker(payload: CreateSpeakerPayload): Promise<SpeakerMutationResponse | ServiceError> {
        return await $fetch<SpeakerMutationResponse>(`/api/client/eventmodule/speaker`, {
            method: 'POST',
            headers: useRequestHeaders(['cookie']),
            body: { clientId: this.clientId, payload },
        }).catch((err) => ({ status: err.statusCode ?? 0, message: err.message }))
    }

    async updateSpeaker(payload: UpdateSpeakerPayload): Promise<SpeakerMutationResponse | ServiceError> {
        return await $fetch<SpeakerMutationResponse>(`/api/client/eventmodule/speaker`, {
            method: 'PUT',
            headers: useRequestHeaders(['cookie']),
            body: { clientId: this.clientId, payload },
        }).catch((err) => ({ status: err.statusCode ?? 0, message: err.message }))
    }

    async getParticipantOverview(eventDataId: number): Promise<EventParticipant[] | ServiceError> {
        return await $fetch<EventParticipant[]>(`/api/client/eventmodule/participants`, {
            headers: useRequestHeaders(['cookie']),
            query: { clientid: this.clientId, eventdataid: eventDataId },
        }).catch((err) => ({ status: err.statusCode ?? 0, message: err.message }))
    }

    async updateParticipant(payload: UpdateParticipantPayload): Promise<ParticipantMutationResponse | ServiceError> {
        return await $fetch<ParticipantMutationResponse>(`/api/client/eventmodule/participant`, {
            method: 'PUT',
            headers: useRequestHeaders(['cookie']),
            body: { clientId: this.clientId, payload },
        }).catch((err) => ({ status: err.statusCode ?? 0, message: err.message }))
    }
}
